import { createElement, useMemo, type ReactElement } from 'react';
import { Badge } from '@/components/ui/badge';
import type { ExtensionSource } from '@kirin/api-client/contracts/extensions';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import {
  getPlatformIcon,
  getPlatformLabel,
} from '@/components/shared/platform/platform-utils';

interface IDEBadgeProps {
  sources: ExtensionSource[];
  direction?: SortOrder;
}

/**
 * Component for displaying IDE badges for platforms that have extensions.
 * Each registered platform renders with its branded icon and `displayName`;
 * unknown sources fall back to a generic Code2 icon with a capitalised label.
 *
 * Single- and multi-source rows render through the same Badge path so they
 * stay visually aligned in tables (KIR-2393).
 */
export function IDEBadge({
  sources,
  direction = SortOrder.Asc,
}: IDEBadgeProps): ReactElement {
  const sortedSources = useMemo(
    () =>
      sources.slice().sort((a, b) => {
        const labelA = getPlatformLabel(a);
        const labelB = getPlatformLabel(b);
        return direction === SortOrder.Asc
          ? labelA.localeCompare(labelB)
          : labelB.localeCompare(labelA);
      }),
    [sources, direction]
  );

  if (sortedSources.length === 0) {
    return <span>—</span>;
  }

  return (
    <div className="flex gap-1 flex-wrap">
      {sortedSources.map(source => (
        <Badge key={source} variant="outline" className="gap-1">
          {createElement(getPlatformIcon(source), {
            className: 'h-3 w-3',
          })}
          {getPlatformLabel(source)}
        </Badge>
      ))}
    </div>
  );
}
